/**
 * ENG-8679 AC2 + AC4 — the cold-start queue for suppression reports.
 *
 * When the new-agent grace suppresses a restart-breaker trip, the manager tells
 * the control plane (`POST /host/circuit-breaker/suppressed`) so the suppression
 * is visible off the host. That report needs an `agent_id`, and the manager
 * learns agent ids from `/host/agents` in `processAgent`.
 *
 * Those two can race. The dashboard restart loop can drive a restart — and
 * therefore a suppression — before `processAgent` has ever run for that agent,
 * so a cold manager knows a suppression happened but not which agent to attach
 * it to. Dropping the report there reintroduces the swallowed-signal defect AC2
 * and AC4 exist to close, one layer down inside the fix for it (CodeRabbit,
 * PR #4351).
 *
 * "It will recur on the next poll" was the original argument for dropping, and
 * it does not hold: a suppression fires on a RESTART, and the restart that
 * produced it can be the last of the episode — the grace works precisely by not
 * restarting again. A suppression at the tail of a flap would then never be
 * reported at all.
 *
 * So it is deferred, mirroring what this codebase already does for trips
 * (ENG-5441 / PR #1302: a trip hydrated before the id lands is retried from
 * `processAgent`, not dropped).
 *
 * This lives in its own module rather than inline in `manager-worker.ts` for a
 * plain reason: `manager-worker.ts` cannot be imported by a test without
 * standing up the whole manager. The queue is where the decisions are, so the
 * queue is what is unit-tested.
 */

/** The report body minus `agent_id`, which is the field we are waiting on. */
export type SuppressionReportPayload = Record<string, unknown>;

/**
 * ENG-9138: which SUPPRESSION this report describes.
 *
 * `new_agent_grace` — ENG-8679's original case: a provisioning-shaped trip
 * withheld because the agent is inside its post-creation grace.
 * `scheduled_rollover_loop` — a platform-scheduled restart class (day-rollover)
 * looping. It never trips at all, so there is no "withheld trip" to report; the
 * report IS the signal.
 *
 * Not a free string: the two produce different alert prose and different audit
 * rows, and a typo'd reason would silently land in the wrong branch.
 */
export type SuppressionReason =
  | 'new_agent_grace'
  | 'scheduled_rollover_loop'
  // ENG-10066 — a human has commanded many restarts of this agent in an hour
  // (the `operator-command` class, i.e. a channel `/restart`). Like the rollover
  // loop this class NEVER trips, so the report is the only way it becomes
  // visible. Unlike the rollover loop it does not mean the platform is broken:
  // it means the AGENT is, badly enough that somebody kept restarting it.
  | 'operator_command_loop';

/**
 * ENG-9138: keyed by agent AND reason, not by agent alone.
 *
 * A single agent can legitimately have both suppressions live at once — a
 * brand-new agent inside its grace whose day rollover is also looping. Keyed by
 * code_name only, the second `queueSuppressionReport` silently overwrote the
 * first and one of the two signals was lost. Losing a signal because another
 * signal arrived is the exact defect class ENG-8679 and ENG-9138 both exist to
 * close, so it must not be reintroduced by the queue that carries them.
 *
 * Still bounded: at most one entry per (agent, reason), and the reason set is a
 * closed union, so a flapping agent cannot grow the map without limit.
 */
const pending = new Map<string, Map<SuppressionReason, SuppressionReportPayload>>();

/**
 * Hold a suppression report until an `agent_id` is known for `codeName`.
 *
 * LAST ONE WINS WITHIN A REASON, deliberately. Successive suppressions of the
 * SAME kind inside one cold-start window describe the same bounded episode for
 * the same agent, and the alert they produce dedupes per agent anyway — so
 * keeping the newest is keeping the one an operator would want (highest count,
 * freshest age, current expiry). Across DIFFERENT reasons nothing is dropped;
 * see the map comment above for why that distinction is load-bearing.
 */
export function queueSuppressionReport(
  codeName: string,
  reason: SuppressionReason,
  payload: SuppressionReportPayload,
): void {
  let byReason = pending.get(codeName);
  if (!byReason) {
    byReason = new Map<SuppressionReason, SuppressionReportPayload>();
    pending.set(codeName, byReason);
  }
  byReason.set(reason, { ...payload, reason });
}

/**
 * Take the queued report for `codeName` if it can be sent now.
 *
 * Returns `null` when nothing is queued, or when `agentId` is still unknown —
 * in which case the entry STAYS queued for a later poll.
 *
 * The entry is removed here, BEFORE the caller's POST, and that is the point of
 * routing this through a "take". A queue that held the entry until a send
 * succeeded would, against a persistently failing endpoint, re-POST the same
 * stale suppression on every poll for the life of the manager — the retry-storm
 * the route's own contract forbids. A dropped report costs one alert; the local
 * suppression, which is the part that keeps the agent running, already applied.
 */
export function takeSuppressionReports(
  codeName: string,
  agentId: string | undefined,
): SuppressionReportPayload[] {
  const byReason = pending.get(codeName);
  if (!byReason || byReason.size === 0) return [];
  // Still cold — EVERY reason stays queued. Taking a subset here would drop the
  // ones we could not send, which is the swallowed-signal defect again.
  if (!agentId) return [];
  const payloads = [...byReason.values()];
  pending.delete(codeName);
  return payloads;
}

/** True when a report for `codeName` is waiting on an agent id. Test/diagnostics. */
export function hasPendingSuppressionReport(codeName: string): boolean {
  return pending.has(codeName);
}

/**
 * Number of reports waiting on an agent id. Test/diagnostics.
 *
 * ENG-9138: counts REPORTS, not agents. `pending.size` would now under-report
 * an agent holding both a grace suppression and a rollover-loop report as one.
 */
export function pendingSuppressionReportCount(): number {
  let total = 0;
  for (const byReason of pending.values()) total += byReason.size;
  return total;
}

/** Drop everything queued. Test hygiene only. */
export function clearPendingSuppressionReports(): void {
  pending.clear();
}
